import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatINR } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import RequireCompany from "@/components/RequireCompany";
import PageHeader from "@/components/PageHeader";
import ExportToolbar from "@/components/ExportToolbar";
import DateRangePicker from "@/components/DateRangePicker";
import { useReportRange, formatRangePretty, formatFYLabel } from "@/lib/useReportRange";
import { exportExcel, exportFinancialPDF } from "@/lib/exporters";
import { Input } from "@/components/ui/input";
import { ArrowUp, ArrowDown, AlertTriangle } from "lucide-react";
import { devError } from "@/lib/logger";

/**
 * Inventory Stock Item Report — period-aware.
 *
 * Per item: opening qty/value, purchase qty/value/GST, sales qty/value/GST,
 * closing qty/value, reorder flag, turnover ratio.
 *
 * UI: date-range picker, item-name search box, total ribbon at the bottom,
 * inline indicators for "below reorder" and "turnover ratio".
 *
 * Backend: GET /api/companies/{cid}/reports/stock-summary?start=...&end=...
 */
function Inner() {
  const navigate = useNavigate();
  const { activeId, active } = useCompany();
  const { range, setRange, applied, apply, params } = useReportRange();
  const dirty = range.start !== applied.start || range.end !== applied.end;
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!activeId) return;
    let mounted = true;
    api
      .get(`/companies/${activeId}/reports/stock-summary`, { params })
      .then((r) => { if (mounted) setData(r.data); })
      .catch((err) => devError("Stock summary load failed", err));
    return () => { mounted = false; };
  }, [activeId, applied.start, applied.end, params]);

  const rows = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data.rows;
    return data.rows.filter((r) =>
      r.name.toLowerCase().includes(q) ||
      (r.group || "").toLowerCase().includes(q) ||
      (r.hsn || "").toLowerCase().includes(q),
    );
  }, [data, search]);

  const onCSV = () => {
    if (!data) return;
    const head = [
      "Item", "Group", "HSN", "Unit", "GST %",
      "Opening Qty", "Opening Value (Rs)",
      "Purchase Qty", "Purchase Taxable", "Purchase GST", "Purchase Total",
      "Sales Qty", "Sales Taxable", "Sales GST", "Sales Total",
      "Closing Qty", "Closing Value (Rs)", "Closing Rate",
      "Reorder Level", "Below Reorder", "Turnover Ratio",
    ];
    const body = data.rows.map((r) => [
      r.name, r.group, r.hsn, r.unit, r.gst_rate,
      r.opening_qty, r.opening_value,
      r.purchase_qty, r.purchase_value, r.purchase_gst, r.purchase_total,
      r.sales_qty, r.sales_value, r.sales_gst, r.sales_total,
      r.closing_qty, r.closing_value, r.closing_rate,
      r.reorder_level, r.below_reorder ? "Yes" : "No", r.turnover_ratio,
    ]);
    exportExcel(`stock-summary-${active?.name || "report"}`, [
      { name: "Stock Summary", rows: [head, ...body] },
    ]);
  };

  const onPDF = () => {
    if (!data) return;
    const head = ["Item (HSN)", "Op Qty", "Pur Qty", "Sales Qty", "Cl Qty", "Cl Value (Rs.)"];
    const pdfRows = data.rows.map((r) => [
      `${r.name}${r.hsn ? ` (${r.hsn})` : ""}`,
      `${r.opening_qty} ${r.unit}`,
      `${r.purchase_qty} ${r.unit}`,
      `${r.sales_qty} ${r.unit}`,
      `${r.closing_qty} ${r.unit}${r.below_reorder ? "  !" : ""}`,
      formatINR(r.closing_value),
    ]);
    pdfRows.push({
      cells: ["TOTAL", "", "", "", "", formatINR(data.totals.closing_value)],
      type: "grand",
    });

    exportFinancialPDF(
      `stock-summary-${active?.name || "report"}`,
      "Inventory Stock Summary",
      [{ title: "", headers: head, rows: pdfRows }],
      {
        company: active?.name,
        address: active?.address,
        phone: active?.phone,
        email: active?.email,
        gstin: active?.gstin,
        pan: active?.pan,
        fy: formatFYLabel(range),
        subtitle: `${formatRangePretty(range)}  -  ${data.item_count} items  -  Quantities at item-specific unit (Kg / Nos / etc.)`,
        date: applied.end,
        place: active?.state || "",
        showSignatures: false,
        orientation: "portrait",
      },
    );
  };

  return (
    <>
      <PageHeader
        title="Inventory Stock Summary"
        subtitle={`${active?.name || ""}  •  ${formatRangePretty(range)}`}
        actions={<ExportToolbar onExcel={onCSV} onPDF={onPDF} onPrint={() => window.print()} />}
      />
      <div className="p-6 space-y-4" data-testid="stock-summary-page">
        <div className="flex flex-wrap items-center gap-3">
          <DateRangePicker start={range.start} end={range.end} onChange={setRange} onApply={apply} dirty={dirty} />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter by item name, group or HSN…"
            className="max-w-xs"
            data-testid="input-search-item"
          />
        </div>

        {!data && <div className="text-sm text-gray-500" data-testid="stock-summary-loading">Loading…</div>}

        {data && (
          <>
            {/* Summary ribbon */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="stock-summary-totals">
              <Stat label="Items" value={String(data.item_count)} />
              <Stat label="Opening Stock" value={`Rs. ${formatINR(data.totals.opening_value)}`} />
              <Stat
                label="Closing Stock"
                value={`Rs. ${formatINR(data.totals.closing_value)}`}
                hint={
                  data.totals.closing_value > data.totals.opening_value
                    ? <span className="text-emerald-600 inline-flex items-center"><ArrowUp className="w-3 h-3" /> Higher than opening</span>
                    : data.totals.closing_value < data.totals.opening_value
                    ? <span className="text-amber-700 inline-flex items-center"><ArrowDown className="w-3 h-3" /> Lower than opening</span>
                    : null
                }
              />
              <Stat
                label="Period GST (Pur + Sales)"
                value={`Rs. ${formatINR(data.totals.purchase_gst + data.totals.sales_gst)}`}
              />
            </div>

            <div className="overflow-auto border border-gray-200 rounded bg-white">
              <table className="w-full text-xs" data-testid="stock-summary-table">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr className="text-[10px] uppercase tracking-wider text-gray-500">
                    <th className="text-left pl-3 py-2">Item</th>
                    <th className="text-left py-2">HSN</th>
                    <th className="text-right py-2" colSpan={2}>Opening</th>
                    <th className="text-right py-2" colSpan={3}>Purchase</th>
                    <th className="text-right py-2" colSpan={3}>Sales</th>
                    <th className="text-right py-2 pr-3" colSpan={2}>Closing</th>
                  </tr>
                  <tr className="text-[9.5px] uppercase tracking-wider text-gray-400 border-t border-gray-100">
                    <th></th><th></th>
                    <th className="text-right py-1.5">Qty</th><th className="text-right py-1.5">Value</th>
                    <th className="text-right py-1.5">Qty</th><th className="text-right py-1.5">Taxable</th><th className="text-right py-1.5">GST</th>
                    <th className="text-right py-1.5">Qty</th><th className="text-right py-1.5">Taxable</th><th className="text-right py-1.5">GST</th>
                    <th className="text-right py-1.5">Qty</th><th className="text-right py-1.5 pr-3">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr><td colSpan={12} className="text-center py-6 text-gray-400 italic">No items match the filter.</td></tr>
                  )}
                  {rows.map((r) => (
                    <tr
                      key={r.id}
                      className="border-b border-gray-100 hover:bg-blue-50/40 cursor-pointer"
                      onClick={() => navigate(`/reports/item-ledger/${r.id}`)}
                      title="Click to open this item's date-wise ledger"
                      data-testid={`row-${r.name}`}
                    >
                      <td className="pl-3 py-2">
                        <div className="font-medium text-blue-700 hover:underline flex items-center gap-1" data-testid={`drill-item-${r.name}`}>
                          {r.name}
                          {r.below_reorder && (
                            <span title={`Below reorder level of ${r.reorder_level} ${r.unit}`} className="text-amber-600">
                              <AlertTriangle className="w-3 h-3" data-testid={`flag-reorder-${r.name}`} />
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-gray-500">
                          {r.group}  •  {r.gst_rate}% GST  •  Turnover {r.turnover_ratio}×
                        </div>
                      </td>
                      <td className="font-mono text-[10px] text-gray-500">{r.hsn || "—"}</td>
                      <td className="text-right font-mono tabular-nums">{r.opening_qty} {r.unit}</td>
                      <td className="text-right font-mono tabular-nums">{formatINR(r.opening_value)}</td>
                      <td className="text-right font-mono tabular-nums">{r.purchase_qty} {r.unit}</td>
                      <td className="text-right font-mono tabular-nums">{formatINR(r.purchase_value)}</td>
                      <td className="text-right font-mono tabular-nums text-blue-700">{formatINR(r.purchase_gst)}</td>
                      <td className="text-right font-mono tabular-nums">{r.sales_qty} {r.unit}</td>
                      <td className="text-right font-mono tabular-nums">{formatINR(r.sales_value)}</td>
                      <td className="text-right font-mono tabular-nums text-blue-700">{formatINR(r.sales_gst)}</td>
                      <td className="text-right font-mono tabular-nums font-semibold">{r.closing_qty} {r.unit}</td>
                      <td className="text-right font-mono tabular-nums font-semibold pr-3">{formatINR(r.closing_value)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                  <tr className="font-semibold text-gray-900 text-[11px]">
                    <td colSpan={3} className="pl-3 py-2">TOTAL ({data.item_count} items)</td>
                    <td className="text-right font-mono tabular-nums">Rs. {formatINR(data.totals.opening_value)}</td>
                    <td></td>
                    <td className="text-right font-mono tabular-nums">Rs. {formatINR(data.totals.purchase_value)}</td>
                    <td className="text-right font-mono tabular-nums text-blue-800">Rs. {formatINR(data.totals.purchase_gst)}</td>
                    <td></td>
                    <td className="text-right font-mono tabular-nums">Rs. {formatINR(data.totals.sales_value)}</td>
                    <td className="text-right font-mono tabular-nums text-blue-800">Rs. {formatINR(data.totals.sales_gst)}</td>
                    <td></td>
                    <td className="text-right font-mono tabular-nums pr-3">Rs. {formatINR(data.totals.closing_value)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="text-[10px] text-gray-400 italic">
              Opening stock at start of period ({(data.valuation_method || "FIFO").replaceAll("_", " ").toLowerCase()} valued). Purchase / Sales quantities are net of returns (Debit Notes / Credit Notes). GST shown is computed at the line GST % applied to taxable value. Closing stock is {(data.valuation_method || "FIFO").replaceAll("_", " ").toLowerCase()}-valued at the end of the period.
            </div>
          </>
        )}
      </div>
    </>
  );
}

function Stat({ label, value, hint }) {
  return (
    <div className="border border-gray-200 rounded bg-white px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-gray-500 font-medium">{label}</div>
      <div className="text-lg font-semibold text-gray-900 mt-0.5 font-mono tabular-nums">{value}</div>
      {hint && <div className="text-[10px] mt-0.5">{hint}</div>}
    </div>
  );
}

export default function StockSummary() {
  return (
    <RequireCompany>
      <Inner />
    </RequireCompany>
  );
}
